<template>
    <div id="lt-yantr-v2" class="advanced-calc cash-out-refinance-v2" :class="{ index: hasHeader || hasSubheader}">
        <h2 v-if="hasHeader" class="green-header">{{ header }}</h2>
        <h1 v-if="hasSubheader" class="text-center">{{ subheader }}</h1>
        <div class="container-width">
            <div class="advanced-calc-filter">
                <div class="filter-inputs">
                    <div>
                        <number-input
                            v-model="HomeValue"
                            :label="labelHomeValue"
                            :tooltip-link="tooltipHomeValue"
                            inputmode="decimal"
                            @input="updateHomeInsurance($event)"
                        />
                    </div>
                    <div>
                        <number-input
                            v-model="MortgageBalance"
                            :label="labelMortgageBalance"
                            :tooltip-link="tooltipMortgageBalance"
                            inputmode="decimal"
                            @input="MortgageBalanceChange($event)"
                        />
                    </div>
                    <div>
                        <number-input
                            v-model="CashOut"
                            :label="labelCashOut"
                            :tooltip-link="tooltipCashOut"
                            inputmode="decimal"
                        />
                    </div>
                    <div>
                        <drop-down
                            v-model="LoanType"
                            :label="labelLoanType"
                            :options="LoanTermInYearsList"
                            :tooltip-link="tooltipLoanType"
                            @input="updateMortgageRate($event)"
                        />
                    </div>
                </div>
                <!-- Advanced Options start -->
                <transition name="slide">
                    <div v-show="ShowAdvancedOptions" class="advanced-options" :class="{ 'all-options': ShowAdvancedOptions, 'less-options' : !ShowAdvancedOptions}">
                        <div class="filter-inputs">
                            <div>
                                <number-input
                                    v-model="MortgageRate"
                                    :label="labelMortgageRate"
                                    tooltip-text="Please enter the estimated mortgage interest rate for your cash-out refinance loan."
                                    symbol="%"
                                    precision="3"
                                    maxValue="100.000"
                                    inputmode="decimal"
                                />
                                <a href="/home/mortgage/rates/" class="font-sm lh-normal">Check out current mortgage rates</a>
                            </div>
                            <div class="inputs-subtext percentage-amt">
                                <number-input
                                    v-model="PropertyTaxes"
                                    :label="labelPropertyTaxes"
                                    inputmode="decimal"
                                    @input="PropertyTaxesChange($event)"
                                />
                                <span class="subtext-val">/yr</span>
                            </div>
                            <div class="percentage-input">
                                <number-input
                                    v-model="PropertyTaxesPercent"
                                    symbol="%"
                                    symbol-aligned="right"
                                    precision="2"
                                    maxValue="100.00"
                                    inputmode="decimal"
                                    @input="PropertyTaxesPercentChange($event)"
                                />
                            </div>
                            <div class="inputs-subtext">
                                <number-input
                                    v-model="HomeInsurance"
                                    :label="labelHomeInsurance"
                                    inputmode="decimal"
                                />
                                <span class="subtext-val">/yr</span>
                            </div>
                            <div class="inputs-subtext">
                                <number-input
                                    v-model="MoHoaFees"
                                    :label="labelHOAFees"
                                    inputmode="decimal"
                                    maxlength="6"
                                />
                                <span class="subtext-val">/mo</span>
                            </div>
                        </div>
                        <div class="controls">
                            <div class="include-pmi">
                                <div class="form-checkbox">
                                    <input id="includePMI" v-model="CheckPMI" type="checkbox" :disabled="ltvIsLessThan80" />
                                    <label for="includePMI">Include PMI</label>
                                    <lt-label :span="span" :tooltip-link="tooltipPMI" />
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>
                <!-- Advanced Options end-->
                <div class="controls">
                    <label class="show-hide-options" @click="viewMoreClick()">
                        <span class="lt-icons" :class="{ 'lt4-MinusCircle': ShowAdvancedOptions, 'lt4-PlusCircle' : !ShowAdvancedOptions}"></span> Advanced Options
                    </label>
                    <div class="reset-sec">
                        <a href="javascript:void(0)" class="reset-calc" @click="resetFilter()"><span class="lt-icons lt4-Refresh"></span> Reset Calculator</a>
                        <advertising-disclosures :disclosure-id="DisclosureId1" :disclosure-text="DisclosureText" :disclosure-tokens="DisclosureTokens" />
                    </div>
                </div>
            </div>
            <div class="offer-btn-sec">
                <h5 :class="{ 'blue-text' : ChangeLabelColor, 'transition': !ChangeLabelColor }">Your new monthly payment will be</h5>
                <button class="main-btn btn-green" type="submit" @click="redirect()">
                    <div class="home-worth text-center">
                        <span class="home-price">${{ currencyWholeNumber(TotalMoPayment) }}</span>
                        <span class="subtext">View offers with similar payments</span>
                    </div>
                    <div class="button-arrow">
                        <span class="lt4-ChevronRight"></span>
                    </div>
                </button>
                <advertising-disclosures :disclosure-id="DisclosureId2" :disclosure-text="DisclosureText" :disclosure-tokens="DisclosureTokens" />
            </div>

            <div v-if="creditScore !== 9 && creditScore !== 10" class="calculation-section">
                <div v-if="ltvIsMoreThan80 && CashOut > 0" class="cash-out-msg">
                    Sorry, you may not be able to cash out ${{ currencyWholeNumber(CashOut) }}. Cash-out refinance requires a maximum combined loan-to-value (CLTV) of 80%. Try changing your cash out amount.
                </div>
                <div class="loan-details">
                    <h5>New loan details</h5>
                    <div class="result-sec">
                        <div class="result-sec--overview">
                            <div class="lt-sec">
                                <label class="homeprice" :class="{ 'red-text': isNegativeAmount}">${{ currencyWholeNumber(TotalMoPayment) }}</label>
                                <label class="payment-label">Monthly Payment</label>
                            </div>
                            <div class="rt-sec">
                                <div class="calc-box">
                                    <div>
                                        <h3 class="value">${{ currencyWholeNumber(unformatFigure(HomeValue)) }}</h3>
                                        <label>Home Value</label>
                                    </div>
                                    <div>
                                        <h3 class="value">${{ currencyWholeNumber(unformatFigure(NewLoanBalance)) }}</h3>
                                        <label>New Loan Balance</label>
                                    </div>
                                    <div>
                                        <h3 class="value">${{ currencyWholeNumber(unformatFigure(CashOut)) }}</h3>
                                        <label>Cash Out</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="result-sec--details">
                            <div class="lt-sec">
                                <button class="btn btn-blue btn-arrow" type="submit" @click="redirect()">{{ DestinationButtonText }}</button>
                                <advertising-disclosures v-if="DisclosureText" :disclosure-id="DisclosureId3" :disclosure-text="DisclosureText" :disclosure-tokens="DisclosureTokens" />
                                <lt-logo v-if="ltLogo==1">
                                    <small>NMLS#1136 Terms & Conditions Apply</small>
                                </lt-logo>
                            </div>
                            <div class="rt-sec">
                                <div class="loan-value">
                                    <div class="percentage">Loan-to-value is at {{ Ltv }}% <lt-label :span="span" :tooltip-link="tooltipLtv" /></div>
                                    <span v-if="ltvIsLessThan80">This makes you eligible to cash-out.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="payment-break month-payment-sec">
                    <h5>How your monthly payment breaks down</h5>
                    <div class="result-sec">
                        <div class="result-sec--overview">
                            <div class="lt-sec">
                                <div class="chart-parent">
                                    <doughnut-chart ref="doughnutChart" type="RoundedDoughnut" :chartData="chartData" :options="options" :width="chartWidth" :height="chartHeight" />
                                    <div class="donut-inner">
                                        <h3>${{ currencyWholeNumber(TotalMoPayment) }}</h3>
                                        <label>Total Monthly Payment</label>
                                    </div>
                                </div>
                            </div>
                            <div class="rt-sec">
                                <div class="monthly-budget">
                                    <ul class="list-unstyled summary">
                                        <li>
                                            <label><span class="green-bg"></span> Principal & Interest</label>
                                            <span class="value">${{ currencyWholeNumber(MoPrincipalIntestrest) }}</span>
                                        </li>
                                        <li>
                                            <label><span class="hopper-green-bg"></span>Property Taxes</label>
                                            <span class="value">${{ currencyWholeNumber(MoPropertyTaxes) }}</span>
                                        </li>
                                        <li>
                                            <label><span class="blue-bg"></span>Homeowner Ins.</label>
                                            <span class="value">${{ currencyWholeNumber(MoHomeInsurance) }}</span>
                                        </li>
                                        <li>
                                            <label><span class="purple-bg"></span>HOA Dues</label>
                                            <span class="value">${{ currencyWholeNumber(MoHoaFees) }}</span>
                                        </li>
                                        <li>
                                            <label><span class="dark-blue-bg"></span> PMI</label>
                                            <span class="value">${{ currencyWholeNumber(MoPMI) }}</span>
                                        </li>
                                    </ul>
                                    <div class="clear-fix"></div>
                                </div>
                                <div class="offer-btn-mob">
                                    <button class="btn btn-blue btn-arrow" type="submit" @click="redirect()">{{ DestinationButtonText }}</button>
                                    <advertising-disclosures v-if="DisclosureText" :disclosure-id="DisclosureId4" :disclosure-text="DisclosureText" :disclosure-tokens="DisclosureTokens" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="low-credit-score">
                <div class="credit-score-msg">
                    <h4>Sorry, typically you will need a 620-640 credit score for Cash Out Refinances</h4>
                    <h4>It looks like a Cash Out Refinance isn't the best solution for your financial situation. Not to worry, though. We have other resources just for you!</h4>
                    <a href="https://spring.lendingtree.com/enroll" target="_blank">Knowledge is power. Get your credit score free now</a>
                </div>
                <ul class="credit-score-info">
                    <li>
                        <i class="lt4-CreditRepair"></i>
                        <h5>Credit repair services</h5>
                        <p>When looking into <a href="/credit-repair/" target="_blank">services to repair and improve your credit</a>, it's important to research as much as possible. You want to make sure you identify and avoid any scams and protect yourself. You can learn more at What You Need to Know About Credit Repair Services.</p>
                        <a href="/credit-repair/what-you-need-to-know-about-credit-repair-services/" class="btn btn-blue" target="_blank">Learn More</a>
                    </li>
                    <li>
                        <i class="lt4-CreditCards"></i>
                        <h5>How to build credit</h5>
                        <p>Perhaps you're just starting out on your own and need to build your credit. Luckily, you can start establishing credit by starting small – such as opening up a student credit card or putting some bills in your own name. Make sure to pay every single bill on time and get in the habit of monitoring your credit score regularly. You can learn more on how to build credit from scratch here.</p>
                        <a href="/credit-repair/how-to-build-credit-from-scratch/" class="btn btn-blue" target="_blank">Learn More</a>
                    </li>
                    <li>
                        <i class="lt4-CreditScore"></i>
                        <h5>Improve your credit</h5>
                        <p>You can improve your credit by paying all of your bills on time, using your credit card wisely, disputing any errors on your credit report, and keeping your credit card balances low. For more, see Simple Ways to Improve Your Credit Rating.</p>
                        <a href="/credit-repair/how-to-improve-your-credit-score/" class="btn btn-blue" target="_blank">Learn More</a>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
    import Accounting from 'accounting';
    import VueScrollTo from 'vue-scrollto';
    import DoughnutChart from '~/components/v2/doughnut-chart.vue';
    import LtLabel from '~/components/v2/lt-label.vue';
    import LoanTermList from '~/constants/v2/loan-term-list';
    import ChartTooltip from '~/helpers/chart-tooltip';
    import PaymentHelper from '~/helpers/v2/payment';
    import BaseMixin from '~/mixin/v2/base-mixin';
    import LtLogo from '~/components/v2/lt-logo.vue';

    export default {
        props: {
            labelHomeValue: {
                default: 'Home Value',
            },
            homeValue: {
                default: 250000,
            },
            tooltipHomeValue: {
                default: 'home-value',
            },
            labelMortgageBalance: {
                default: 'Mortgage Balance',
            },
            mortgageBalance: {
                default: 200000,
            },
            tooltipMortgageBalance: {
                default: 'current-balance',
            },
            creditScore: {
                default: 2,
                type: Number,
            },
            labelCashOut: {
                default: 'Cash-Out',
            },
            cashOut: {
                default: 0,
            },
            tooltipCashOut: {
                default: 'cash-out-refinancing',
            },
            labelMortgageRate: {
                default: 'Mortgage Rate',
            },
            mortgageRate: {
                default: '4.250',
            },
            labelLtv: {
                default: 'Loan-To-Value: (LTV)',
            },
            ltv: {
                default: 80,
            },
            tooltipLtv: {
                default: 'loan-to-value-ratio',
            },
            labelPropertyTaxes: {
                default: 'Property Taxes',
            },
            propertyTaxes: {
                default: 3000,
            },
            propertyTaxesPercent: {
                default: 1.20,
            },
            labelHomeInsurance: {
                default: 'Home Insurance',
            },
            homeInsurance: {
                default: 875,
            },
            homeInsurancePerc: {
                default: '0.35',
            },
            labelHOAFees: {
                default: 'HOA Fees',
            },
            moHoaFees: {
                default: 0,
            },
            labelLoanType: {
                default: 'Loan Term',
            },
            loanType: {
                default: 30,
            },
            tooltipLoanType: {
                default: 'loan-terms',
            },
            tooltipPMI: {
                default: 'private-mortgage-insurance',
            },
            ltLogo: {
                default: 0,
            },
            disclosureTokens: {
                default: '01',
            },
            disclosureText: {
                default: 'Disclosures',
            },
            disclosureId1: {
                default: 'Disclosures1',
            },
            disclosureId2: {
                default: 'Disclosures2',
            },
            disclosureId3: {
                default: 'Disclosures3',
            },
            disclosureId4: {
                default: 'Disclosures4',
            },
            showAdvancedOptions: {
                default: false,
            },
            changeLabelColor: {
                default: false,
            },
            customizeButtonText: {
                default: 'Customize',
            },
            recalculateButtonText: {
                default: 'Recalculate',
            },
            destinationLinkOrId: {
                default: 'wp-refinance',
            },
            destinationButtonText: {
                default: 'View Free Offers',
            },
            span: {
                type: String,
                default: '',
            },
            maxValue: {
                default: 999999999,
            },
            header: {
                default: '',
            },
            subheader: {
                default: '',
            },
        },
        data() {
            return {
                hasHeader: false,
                hasSubheader: false,
                HomeValue: this.homeValue,
                MortgageBalance: this.mortgageBalance,
                CashOut: this.cashOut,
                MortgageRate: parseFloat(this.mortgageRate),
                Ltv: this.ltv,
                PropertyTaxes: parseFloat(this.propertyTaxes),
                PropertyTaxesPercent: this.propertyTaxesPercent,
                HomeInsurance: this.homeInsurance,
                HomeInsurancePerc: parseFloat(this.homeInsurancePerc),
                LoanType: this.loanType,
                LoanTermInYearsList: LoanTermList.LoanTermInYearsList.va,
                DisclosureText: this.disclosureText,
                DisclosureId1: this.disclosureId1,
                DisclosureId2: this.disclosureId2,
                DisclosureId3: this.disclosureId3,
                DisclosureId4: this.disclosureId4,
                DisclosureTokens: this.disclosureTokens,
                ShowAdvancedOptions: this.showAdvancedOptions,
                ChangeLabelColor: this.changeLabelColor,
                CustomizeButtonText: this.customizeButtonText,
                RecalculateButtonText: this.recalculateButtonText,
                CheckPMI: true,
                NewLoanBalance: '',
                TotalMoPayment: '',
                MoPrincipalIntestrest: '',
                MoPropertyTaxes: '',
                MoHomeInsurance: '',
                MoHoaFees: this.moHoaFees,
                MoPMI: '',
                Rate: '',
                LoanTerm: '',
                AnnualPropertyTax: '',
                LoanAmount: '',
                LoanToValue: '',

                // Setting for chart
                chartData: {
                    labels: [],
                    datasets: [
                        {
                            data: [],
                            backgroundColor: [
                                '#08c177',
                                '#00b29f',
                                '#00aeef',
                                '#800080',
                                '#101f30',
                            ],
                            borderWidth: 0,
                        },
                    ],
                },
                options: {
                    cutoutPercentage: 85,
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        enabled: false,
                        custom: ChartTooltip.customTooltips,
                    },
                },
                chartWidth: 200,
                chartHeight: 130,
                CurrentDate: new Date(),
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
            };
        },
        computed: {
            isNegativeAmount() {
                return this.TotalMoPayment < 0;
            },
            ltvIsLessThan80() {
                return this.unformatFigure(this.Ltv) < 80;
            },
            ltvIsMoreThan80() {
                return this.unformatFigure(this.Ltv) > 80;
            },
        },
        methods: {
            unformat(val) {
                return Accounting.unformat(val);
            },
            calculateAndViewMore() {
                this.viewMoreClick();
            },
            capitalize(value) {
                let data = '';
                if (!value) return '';
                data = value.toString();
                return data.charAt(0).toUpperCase() + data.slice(1);
            },
            currencyFilterWithPrecision(figure) {
                return figure > 0 ? Accounting.formatNumber(figure, { precision: 2 }) : 0;
            },
            currencyWholeNumber(figure) {
                return figure > 0 ? Accounting.formatNumber(figure) : 0;
            },
            viewMoreClick() {
                this.ShowAdvancedOptions = !this.ShowAdvancedOptions;
                setTimeout(() => {
                    this.ChangeLabelColor = !this.ChangeLabelColor;
                }, 500);
            },
            resetFilter() {
                this.HomeValue = this.formatFigure(this.homeValue);
                this.MortgageBalance = this.formatFigure(this.mortgageBalance);
                this.MortgageBalanceChange(this.MortgageBalance);
                this.CashOut = this.formatFigure(this.cashOut);
                this.MortgageRate = Accounting.formatNumber(this.mortgageRate, { precision: 3 });
                this.Ltv = this.formatFigure(this.ltv);
                this.PropertyTaxes = Accounting.formatNumber(this.propertyTaxes);
                this.PropertyTaxesPercent = Accounting.formatNumber(this.propertyTaxesPercent, { precision: 2 });
                this.HomeInsurance = this.formatFigure(this.homeInsurance);
                this.MoHoaFees = this.formatFigure(this.moHoaFees);
                this.LoanType = this.loanType;
                this.CheckPMI = true;
            },
            currencyFilter(number) {
                return Accounting.formatNumber(number);
            },
            calculatePayment() {
                this.Rate = this.MortgageRate / 1200;
                this.LoanTerm = this.LoanType * 12;
                this.MoHomeInsurance = Math.round(this.unformatFigure(this.HomeInsurance) / 12);
                this.AnnualPropertyTax = this.PropertyTaxesPercent / 1200;
                this.LoanAmount = this.unformatFigure(this.MortgageBalance) + this.unformatFigure(this.CashOut);
                this.LoanToValue = (this.unformatFigure(this.MortgageBalance) + this.unformatFigure(this.CashOut)) / this.unformatFigure(this.HomeValue);
                this.MoPropertyTaxes = Math.round(this.unformatFigure(this.HomeValue) * this.AnnualPropertyTax);
                this.MoPrincipalIntestrest = Math.round(PaymentHelper.compute_PMT(this.LoanAmount, this.Rate, this.LoanTerm));

                if (this.CheckPMI) {
                    this.MoPMI = Math.round(PaymentHelper.compute_PMI(this.LoanToValue, this.LoanAmount));
                } else {
                    this.MoPMI = 0;
                }
                this.Ltv = Accounting.formatNumber(Math.round(this.LoanToValue * 100));
                this.NewLoanBalance = this.unformatFigure(this.MortgageBalance) + this.unformatFigure(this.CashOut);
                this.TotalMoPayment = this.MoPrincipalIntestrest + this.MoPropertyTaxes + this.MoHomeInsurance + parseFloat(this.MoHoaFees) + this.MoPMI;
                this.generateGraph();
            },
            updateMortgageRate(value) {
                this.MortgageRate = value === '15' ? 3.750 : 4.250;
            },
            updateHomeInsurance(val) {
                if (this.unformat(val) > this.unformat(this.maxValue)) {
                    this.HomeValue = this.maxValue;
                }
                this.InitializeData();
            },
            MortgageBalanceChange(value) {
                if (this.unformat(value) > this.unformat(this.maxValue)) {
                    this.MortgageBalance = this.maxValue;
                }
            },
            InitializeData() {
                const updatedHomeInsurance = this.unformat(this.HomeValue) * (this.HomeInsurancePerc / 100);
                this.HomeInsurance = Accounting.formatNumber(updatedHomeInsurance);
                this.PropertyTaxesPercentChange(this.PropertyTaxesPercent);
            },
            generateGraph() {
                const piPercent = Math.round(this.MoPrincipalIntestrest / this.TotalMoPayment * 100);
                const propTaxPercent = Math.round(this.MoPropertyTaxes / this.TotalMoPayment * 100);
                const hoiPercent = Math.round(this.MoHomeInsurance / this.TotalMoPayment * 100);
                const pmiPercent = Math.round(this.MoPMI / this.TotalMoPayment * 100);

                this.chartData.datasets[0].data = [];
                this.chartData.labels = [];
                this.chartData.datasets[0].data.push(piPercent);
                this.chartData.datasets[0].data.push(propTaxPercent);
                this.chartData.datasets[0].data.push(hoiPercent);
                this.chartData.datasets[0].data.push(pmiPercent);

                this.chartData.labels.push(`Principal & Interest ${Accounting.formatMoney(this.MoPrincipalIntestrest, { precision: 0 })}`);
                this.chartData.labels.push(`Property Taxes ${Accounting.formatMoney(this.MoPropertyTaxes, { precision: 0 })}`);
                this.chartData.labels.push(`Homeowner's Insurance ${Accounting.formatMoney(this.MoHomeInsurance, { precision: 0 })}`);
                this.chartData.labels.push(`HOA Dues ${Accounting.formatMoney(this.MoHoaFees, { precision: 0 })}`);
                this.chartData.labels.push(`PMI ${Accounting.formatMoney(this.MoPMI, { precision: 0 })}`);
                this.$refs.doughnutChart.update();
            },
            PropertyTaxesChange(value) {
                let propertyTaxesValue = value;
                if (this.unformat(value) > this.unformat(this.HomeValue)) {
                    this.PropertyTaxes = Accounting.formatNumber(this.HomeValue);
                } else if (this.unformat(value) > this.unformat(this.maxValue)) {
                    this.PropertyTaxes = this.maxValue;
                    propertyTaxesValue = this.maxValue;
                }
                this.PropertyTaxesPercent = this.getPercentageAmount(propertyTaxesValue);
            },
            PropertyTaxesPercentChange(value) {
                this.PropertyTaxes = Accounting.formatNumber(this.getDollarAmount(value));
                if (this.unformat(this.PropertyTaxes) > this.unformat(this.HomeValue)) {
                    this.PropertyTaxes = Accounting.formatNumber(this.HomeValue);
                }
            },
            getDollarAmount(value) {
                let calculatedValue = this.unformat(value) / 100 * this.unformat(this.HomeValue);
                calculatedValue = calculatedValue.toFixed(2) % 1 === 0 ? Math.round(calculatedValue) : calculatedValue.toFixed(2);
                return isNaN(calculatedValue) || calculatedValue === 'Infinity' ? 0 : calculatedValue;
            },
            getPercentageAmount(value) {
                let calculatedValue = this.unformat(value) / this.unformat(this.HomeValue) * 100;
                calculatedValue = calculatedValue.toFixed(2);
                return isNaN(calculatedValue) || calculatedValue === 'Infinity' ? 0 : calculatedValue;
            },
        },
        mounted() {
            this.calculatePayment();
            this.InitializeData();
            if (this.header === '') {
                this.hasHeader = false;
            } else {
                this.hasHeader = true;
            }
            if (this.subheader === '') {
                this.hasSubheader = false;
            } else {
                this.hasSubheader = true;
            }
        },
        watch: {
            HomeValue: 'calculatePayment',
            MortgageBalance: 'calculatePayment',
            CashOut: 'calculatePayment',
            MortgageRate: 'calculatePayment',
            PropertyTaxesPercent: 'calculatePayment',
            HomeInsurance: 'calculatePayment',
            MoHoaFees: 'calculatePayment',
            LoanType: 'calculatePayment',
            CheckPMI: 'calculatePayment',
            Ltv() {
                this.CheckPMI = this.unformatFigure(this.Ltv) >= 80;
            },
        },
        components: {
            LtLabel,
            DoughnutChart,
            LtLogo,
        },
        directives: {
            VueScrollTo,
        },
        mixins: [BaseMixin],
    };
</script>
<style src="../../assets/styles/v2/common.scss" lang="scss"></style>
<style src="../../assets/styles/v2/calc-advanced-filter.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
